import React, { useState, useEffect } from 'react';
import { CheckCircle, Server, Zap, Shield, TrendingUp, Clock, Globe, ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import { getStorageUrl } from '../utils/imageUrlUpdater';

const ProxyPrivate: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentGallerySlide, setCurrentGallerySlide] = useState(0);

  // Set page title and canonical URL
  useEffect(() => {
    document.title = 'Proxy Private - VMon Cloud';
    
    // Set canonical URL
    const existingCanonical = document.querySelector('link[rel="canonical"]');
    if (existingCanonical) {
      existingCanonical.remove();
    }
    
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = 'https://vmon.vn/proxy-private';
    document.head.appendChild(canonical);
    
    return () => {
      if (canonical.parentNode) {
        canonical.parentNode.removeChild(canonical);
      }
      document.title = 'VMon Cloud - Dịch vụ VPS và Hosting chất lượng cao';
    };
  }, []);

  const countries = [
    {
      name: 'Việt Nam',
      flag: '🇻🇳',
      price: '50,000',
      locations: ['Hà Nội', 'TP.HCM'],
      speed: 'Cao',
      description: 'Proxy Việt Nam với tốc độ cao, phù hợp cho các dự án trong nước'
    },
    {
      name: 'Singapore',
      flag: '🇸🇬',
      price: '50,000',
      locations: ['Singapore'],
      speed: 'Rất cao',
      description: 'Proxy Singapore tốc độ cao, kết nối tốt với khu vực Đông Nam Á'
    },
    {
      name: 'United States',
      flag: '🇺🇸',
      price: '50,000',
      locations: ['New York', 'Los Angeles', 'Chicago'],
      speed: 'Cao',
      description: 'Proxy US với nhiều vị trí, phù hợp cho thị trường Bắc Mỹ'
    },
    {
      name: 'United Kingdom',
      flag: '🇬🇧',
      price: '50,000',
      locations: ['London', 'Manchester'],
      speed: 'Cao',
      description: 'Proxy UK chất lượng cao, phù hợp cho thị trường châu Âu'
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'BẢO MẬT TUYỆT ĐỐI',
      description: 'Proxy private chỉ dành riêng cho bạn, không chia sẻ với ai khác. Đảm bảo tính riêng tư và bảo mật cao nhất cho hoạt động online của bạn.'
    },
    {
      icon: Zap,
      title: 'TỐC ĐỘ CAO',
      description: 'Kết nối tốc độ cao với băng thông không giới hạn. Proxy được tối ưu hóa để đảm bảo hiệu suất tốt nhất cho mọi ứng dụng.'
    },
    {
      icon: Globe,
      title: 'ĐA DẠNG VỊ TRÍ',
      description: 'Nhiều quốc gia và thành phố khác nhau. Dễ dàng thay đổi IP theo vị trí địa lý mong muốn.'
    },
    {
      icon: Server,
      title: 'HOẠT ĐỘNG 24/7',
      description: 'Proxy hoạt động ổn định 24/7 với uptime 99.9%. Hỗ trợ kỹ thuật chuyên nghiệp sẵn sàng giải đáp mọi thắc mắc.'
    }
  ];

  const useCases = [
    {
      icon: Globe,
      title: 'Web Scraping',
      description: 'Thu thập dữ liệu từ website một cách hiệu quả và ẩn danh'
    },
    {
      icon: Shield,
      title: 'Bảo mật duyệt web',
      description: 'Ẩn địa chỉ IP thật khi duyệt web để bảo vệ quyền riêng tư'
    },
    {
      icon: MapPin,
      title: 'Truy cập nội dung',
      description: 'Truy cập nội dung bị chặn theo vùng địa lý'
    },
    {
      icon: Server,
      title: 'SEO & Marketing',
      description: 'Kiểm tra ranking website từ nhiều vị trí khác nhau'
    }
  ];

  const specifications = [
    { label: 'Protocol Support', value: 'HTTP/HTTPS, SOCKS5' },
    { label: 'Authentication', value: 'Username:Password' },
    { label: 'Concurrent Connections', value: 'Unlimited' },
    { label: 'Bandwidth', value: 'High Speed, No Throttling' },
    { label: 'Uptime Guarantee', value: '99.9% SLA' },
    { label: 'Setup Time', value: 'Instant Activation' },
    { label: 'IP Rotation', value: 'Manual & Automatic' },
    { label: 'Support', value: '24/7 Technical Support' }
  ];

  const faqItems = [
    {
      question: 'Proxy Private là gì?',
      answer: 'Proxy Private là máy chủ proxy chỉ dành riêng cho một người dùng, không chia sẻ với ai khác. Điều này đảm bảo tốc độ cao, bảo mật tốt và tính ổn định cao hơn so với proxy public.'
    },
    {
      question: 'Sự khác biệt giữa HTTP và SOCKS5?',
      answer: 'HTTP proxy chỉ hỗ trợ traffic web (HTTP/HTTPS), trong khi SOCKS5 hỗ trợ mọi loại traffic và có tính năng bảo mật cao hơn. Proxy của chúng tôi hỗ trợ cả hai giao thức, bạn có thể sử dụng linh hoạt tùy theo nhu cầu.'
    },
    {
      question: 'Proxy có giới hạn băng thông không?',
      answer: 'Không, proxy của chúng tôi không giới hạn băng thông. Bạn có thể sử dụng với tốc độ tối đa mà hạ tầng cho phép.'
    },
    {
      question: 'Làm sao để cấu hình proxy?',
      answer: 'Sau khi mua, bạn sẽ nhận được thông tin proxy qua email. Chúng tôi cũng cung cấp hướng dẫn chi tiết cách cấu hình cho các ứng dụng phổ biến với cả HTTP và SOCKS5.'
    },
    {
      question: 'Có hỗ trợ API không?',
      answer: 'Có, chúng tôi cung cấp API để quản lý proxy tự động, kiểm tra trạng thái và thống kê sử dụng.'
    }
  ];

  const testimonials = [
    getStorageUrl('feedback/feedback_1.jpg'),
    getStorageUrl('feedback/feedback_2.jpg'),
    getStorageUrl('feedback/feedback_3.jpg'),
    getStorageUrl('feedback/feedback_4.jpg'),
    getStorageUrl('feedback/feedback_5.jpg'),
    getStorageUrl('feedback/feedback12.jpg'),
    getStorageUrl('feedback/feedback16.jpg'),
    getStorageUrl('feedback/feedback17.jpg')
  ];

  // Auto-slide functionality
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % testimonials.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 mr-2" />
              <span className="text-2xl font-semibold">Proxy Private</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Proxy Private Chất Lượng Cao
            </h1>
            <p className="text-xl mb-8 max-w-3xl mx-auto">
              Proxy private tốc độ cao, bảo mật tuyệt đối với nhiều quốc gia. 
              Hỗ trợ đồng thời cả HTTP/HTTPS và SOCKS5. Hoàn hảo cho web scraping, SEO, marketing và bảo vệ quyền riêng tư.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Zap className="h-4 w-4 mr-2" />
                Tốc độ cao
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Shield className="h-4 w-4 mr-2" />
                100% Private
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Globe className="h-4 w-4 mr-2" />
                HTTP & SOCKS5
              </div>
              <div className="flex items-center bg-blue-500 bg-opacity-50 px-4 py-2 rounded-full">
                <Server className="h-4 w-4 mr-2" />
                Nhiều quốc gia
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Bảng Giá Proxy Private</h2>
            <p className="text-lg text-gray-600">Hỗ trợ đồng thời cả HTTP/HTTPS và SOCKS5</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6 max-w-2xl mx-auto">
              <p className="text-blue-800 font-semibold">✨ Một proxy - Hai giao thức</p>
              <p className="text-blue-700 text-sm">Sử dụng linh hoạt HTTP/HTTPS hoặc SOCKS5 tùy theo nhu cầu</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {countries.map((country, index) => (
              <div 
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden transition-transform hover:scale-105"
              >
                <div className="p-6">
                  <div className="flex items-center justify-center mb-4">
                    <span className="text-4xl mr-3">{country.flag}</span>
                    <h3 className="text-xl font-bold text-gray-900">{country.name}</h3>
                  </div>
                  
                  <div className="mb-6 text-center">
                    <div className="flex items-baseline justify-center">
                      <span className="text-3xl font-bold text-blue-600">{country.price}</span>
                      <span className="text-gray-600 ml-1">đ/tháng</span>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600 text-right">Vị trí:</div>
                      <div className="font-semibold text-left">{country.locations.join(', ')}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600 text-right">Tốc độ:</div>
                      <div className="font-semibold text-left">{country.speed}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600 text-right">Giao thức:</div>
                      <div className="font-semibold text-left">HTTP/HTTPS, SOCKS5</div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600 text-right">Băng thông:</div>
                      <div className="font-semibold text-left">Không giới hạn</div>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-6 text-center">
                    {country.description}
                  </p>

                  <a 
                    href="https://cloud.vmon.vn/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 rounded-lg font-semibold transition-colors flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Đăng Ký
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Bulk Pricing */}
          <div className="mt-12 bg-white rounded-lg shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              Ưu đãi khi mua số lượng lớn
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 mb-2">5-10 Proxy</div>
                <p className="text-blue-600 font-medium">Giảm 5%</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 mb-2">11-50 Proxy</div>
                <p className="text-blue-600 font-medium">Giảm 15%</p>
              </div>
              <div className="text-center p-4 border border-gray-200 rounded-lg">
                <div className="text-lg font-semibold text-gray-900 mb-2">50+ Proxy</div>
                <p className="text-blue-600 font-medium">Giảm 25%</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Protocol Comparison */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">So Sánh Giao Thức HTTP vs SOCKS5</h2>
            <p className="text-lg text-gray-600">Cả hai giao thức đều được hỗ trợ trong mọi proxy</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Globe className="h-8 w-8 text-blue-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">HTTP/HTTPS Proxy</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Tối ưu cho duyệt web và HTTP requests</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Hỗ trợ HTTPS với SSL/TLS</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Dễ cấu hình trên browser</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Phù hợp cho web scraping</span>
                </li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <Shield className="h-8 w-8 text-green-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">SOCKS5 Proxy</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Hỗ trợ mọi loại traffic (TCP/UDP)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Bảo mật cao hơn với authentication</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Phù hợp cho ứng dụng đa dạng</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">Hỗ trợ P2P và gaming</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Tại Sao Chọn Proxy Private Của Chúng Tôi?</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Ứng Dụng Của Proxy Private</h2>
            <p className="text-lg text-gray-600">Proxy private phù hợp cho nhiều mục đích sử dụng khác nhau</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Feedback Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">KHÁCH HÀNG NÓI GÌ VỀ CHÚNG TÔI?</h2>
            <p className="text-lg text-gray-600 max-w-4xl mx-auto">
              Tại VMon Cloud, ý kiến của khách hàng được đặt lên hàng đầu và chúng tôi luôn sẵn sàng lắng nghe và tiếp thu phản hồi để không ngừng cải thiện chất lượng sản phẩm và dịch vụ. Điều này giúp chúng tôi đáp ứng tốt hơn nhu cầu và mong muốn của khách hàng một cách tối đa.
            </p>
          </div>

          {/* Testimonial Slider */}
          <div className="relative max-w-4xl mx-auto">
            {/* Main Slider */}
            <div className="relative overflow-hidden rounded-lg shadow-lg bg-white">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="w-full flex-shrink-0 flex justify-center items-center p-4">
                    <img
                      src={testimonial}
                      alt={`Customer Feedback ${index + 1}`}
                      className="max-w-full max-h-96 object-contain rounded-lg shadow-sm"
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={prevSlide}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
              >
                <ChevronLeft className="h-6 w-6 text-gray-700" />
              </button>
              <button
                onClick={nextSlide}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-80 hover:bg-opacity-100 rounded-full p-2 shadow-lg transition-all duration-200"
              >
                <ChevronRight className="h-6 w-6 text-gray-700" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="flex justify-center mt-6 space-x-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-200 ${
                    index === currentSlide 
                      ? 'bg-blue-600 scale-110' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Specifications Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Thông Số Kỹ Thuật</h2>
            <p className="text-lg text-gray-600">Chi tiết cấu hình và tính năng proxy</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {specifications.map((spec, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-4">
                <div className="text-sm text-gray-600 mb-1">{spec.label}</div>
                <div className="font-semibold text-gray-900">{spec.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Câu Hỏi Thường Gặp</h2>
            <p className="text-lg text-gray-600">
              Những câu hỏi phổ biến về dịch vụ Proxy Private
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <details key={index} className="bg-white rounded-lg p-6 shadow">
                <summary className="font-semibold text-gray-900 cursor-pointer hover:text-blue-600">
                  {item.question}
                </summary>
                <p className="mt-4 text-gray-600">{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Bắt Đầu Sử Dụng Proxy Private Ngay Hôm Nay
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Trải nghiệm proxy private chất lượng cao với hỗ trợ đồng thời HTTP/HTTPS và SOCKS5
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="https://cloud.vmon.vn/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors inline-block"
            >
              Mua ngay
            </a>
            <a 
              href="https://cloud.vmon.vn/"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors inline-block"
            >
              Liên hệ tư vấn
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProxyPrivate;